import {useTaggingClick} from "@/hooks/common/useTagging";
import {getCookie, getTarget} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Recently from "@COMPONENTS/layout/floating/Desktop/Recently";
import GNB from "@COMPONENTS/layout/header/Desktop/Gnb";
import SearchArea from "@COMPONENTS/layout/header/Desktop/SearchArea";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import * as style from "@FRAMEWORK/ui/desktop/header/index";
import {Accessibility} from "@FRAMEWORK/ui/desktop/text";
import useSigninUser from "@QUERIES/account/useSigninUser";
import useGlobalOpt from "@QUERIES/bff/global/useGlobalOpt";
import useGetRecently from "@QUERIES/essential/user/useGetRecently";
import {useRouter} from "next/router";
import {useEffect, useRef, useState} from "react";

const HeaderComponent = () => {
	const router = useRouter();

	/** 로그인 정보 */
	const signin = useSigninUser({throwOnError: false});
	const webCustNo = signin.data?.body.webCustNo || "";
	const custNmKor = signin.data?.body.custNmKor || "";
	const signinCode = signin.data?.code || "9999";

	/** 최근 본 관련 */
	const userId = getCookie("userId") || (window.ensureUserHasUniqueId ? window.ensureUserHasUniqueId() : "");
	const recentlyListFetch = useGetRecently(userId, webCustNo, {
		enabled: !signin.isFetching && !!userId,
		throwOnError: false,
	});
	const recently = recentlyListFetch.data?.body?.productResultList?.at(0) || null;

	const [recentlyOpen, setRecentlyOpen] = useState(false);
	const [recentlyShow, setRecentlyShow] = useState(false);
	/** routing 시 최근 본 상품 닫기  */
	const handleRouteChange = () => {
		setRecentlyShow(false);
		setRecentlyOpen(false);
	};

	useEffect(() => {
		router.events.on("routeChangeComplete", handleRouteChange);
		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
	}, [router]);

	/** 운영 메뉴 관련 */
	const globalOptFetch = useGlobalOpt();
	const globalOpt = globalOptFetch.data?.body;

	/** publish */
	const [showTooltip, setShowTooltip] = useState(false);
	const [isDim, setIsisDim] = useState(false);
	const handleDim = (is: boolean) => setIsisDim(is);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const loginRef = useRef<HTMLLIElement | null>(null);

	// 로그인시 나오는 툴팁
	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as Element | null;

		if (target && !tooltipRef.current?.contains(target) && !loginRef.current?.contains(target)) {
			setShowTooltip(false);
		}
	};

	useEffect(() => {
		if (showTooltip) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showTooltip]);

	const handleClick = () => {
		setShowTooltip(prev => !prev);
	};

	const {taggingClick} = useTaggingClick("pkg_select_icon_main");

	return (
		<>
			{/* 최근 본 상품 */}
			<Recently
				open={recentlyOpen}
				show={recentlyShow}
				closeShow={() => setRecentlyShow(false)}
				closeOpen={() => setRecentlyOpen(false)}
			/>
			<style.HeaderWrap>
				{/* TopBanner */}
				{/* <TopBanner /> */}
				<style.Container>
					<style.SkipNavi>
						<a href="#content">본문 바로가기</a>
						<a href="#gnb">카테고리 메뉴 바로가기</a>
						<a href="#gnb-login">로그인 메뉴 바로가기</a>
					</style.SkipNavi>
					<style.Gnb>
						<style.TopNavWrap>
							<div>
								<h1>
									<a href={publicRuntimeConfig.ENV_PORTAL_URL}>
										<img src="https://cimgcdn.ybtour.co.kr/common/logo/logo_l.png" alt="노랑풍선" />
									</a>
								</h1>
								{/* 검색 영역 */}
								<SearchArea />
							</div>
							<style.UtileLinks>
								<ul>
									{/* 운영 메뉴 */}
									{globalOpt &&
										globalOpt.map((item, index) => (
											<li key={index}>
												<a
													href={item.pcMenuUrl}
													target={getTarget(item.webMenuTarget)}
													onClick={() => taggingClick(item)}
												>
													{item.pcMenuNm}
												</a>
											</li>
										))}
								</ul>
								<style.BtnGroup>
									<style.BtnMypage
										href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/index.yb`}
										onClick={() => {
											const url = `${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/index.yb`;
											if (signinCode !== "0000") LoginUtil.login(url);
										}}
									>
										<Accessibility>마이페이지</Accessibility>
									</style.BtnMypage>
									<style.BtnRecent
										href="#"
										$hasRecent={!!recently}
										onClick={() => {
											setRecentlyOpen(true);
											setRecentlyShow(true);
										}}
									>
										<Accessibility>최근본</Accessibility>
										{recently?.imgUrl && <img src={recently.imgUrl} alt="상품 이미지" />}
									</style.BtnRecent>
								</style.BtnGroup>
							</style.UtileLinks>
						</style.TopNavWrap>
						{/* GNB */}
						<GNB handleDim={handleDim} />
						{/* GNB */}
						<style.GnbUtil id="gnb-login">
							<style.GnbUtilWrap>
								<ul>
									{signinCode === "0000" ? (
										<>
											<style.Login onClick={handleClick} $isShow={showTooltip} ref={loginRef}>
												{custNmKor} 님
												{showTooltip && (
													<style.LoginTooltip ref={tooltipRef}>
														<a href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/accountPwReaffirmScreen.yb`}>
															개인정보 수정
														</a>
														<a href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/accountPwUpdateScreen.yb`}>
															비밀번호 변경
														</a>
														<a href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/snsConnectionSetup.yb`}>
															SNS 연결 설정
														</a>
													</style.LoginTooltip>
												)}
											</style.Login>
											<li>
												<a
													href="#"
													onClick={e => {
														e.preventDefault();
														LoginUtil.logout();
													}}
												>
													로그아웃
												</a>
											</li>
										</>
									) : (
										<>
											<li>
												<a
													href="#"
													onClick={e => {
														e.preventDefault();
														LoginUtil.login();
													}}
												>
													로그인
												</a>
											</li>
											<li>
												<a href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/index.yb`}>회원가입</a>
											</li>
										</>
									)}
									<li>
										<a href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/resvInfo.yb`}>예약확인/결제</a>
									</li>
									<li>
										<a href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/cs/main.yb`}>고객센터</a>
									</li>
								</ul>
							</style.GnbUtilWrap>
						</style.GnbUtil>
					</style.Gnb>
				</style.Container>
				{isDim && <style.Dim />}
			</style.HeaderWrap>
		</>
	);
};

const Header = () => (
	<ErrorBoundary logMsg="Header Error">
		<HeaderComponent />
	</ErrorBoundary>
);

export default Header;
